import React, {useEffect, useState} from 'react'
import {NumberInput} from "atmosfair-ui";
import DateRangePicker, {
    DATE_RANGE_PICKER_END_DATE,
    DATE_RANGE_PICKER_START_COUNT,
    DATE_RANGE_PICKER_START_DATE,
    DATE_RANGE_PICKER_START_USE_DATES
} from "../../date-range-picker/DateRangePicker";
import "./styles.css"
import {JOURNEY_PROPERTIES, VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import {useTranslation} from "react-i18next";
import {JourneyDatesProps} from "../../save-bundle-create-journey-dialogue/SaveBundleCreateJourneyDialogue";
import {DurationProps} from "../../../types-enums/activity-common-properties";
import {DnDContainerProps} from '../../../container/journey/journey-dnd-container/JourneyDnDContainer';

export interface ValidPersonCountDateFormProps {
    journeyDuration: DurationProps | null,
    personCount: number | null
}

interface PersonCountDateFormProps {
    defaultValues: {
        personCount: number | null,
        journeyDuration: DurationProps | null
    },
    validateForm: number,
    onFormIsValid: (data: ValidPersonCountDateFormProps) => void,
    setCalendarOpen?: (value: boolean) => void,
    setSelectedJourneyDuration?: (value: DnDContainerProps["journeyDuration"]) => void,
}

interface PersonCountDateFormFormDataProps {
    journeyDates: JourneyDatesProps,
    personCount: number
}

const MIN_NUMBER_PERSON_COUNT_JOURNEY = 1
const {
    minNumberPeople,
    maxNumberPeople
} = JOURNEY_PROPERTIES

const MIN_DAYS = 1
const MAX_DAYS = 1000

const getJourneyDuration = (
    journeyWithDates: boolean,
    journeyDates: JourneyDatesProps,
    journeyDaysCount: number
) => {
    const {startDate: journeyStartDate, endDate: journeyEndDate} = journeyDates
    return {
        dates: journeyWithDates ? {
            startDate: journeyStartDate,
            endDate: journeyEndDate
        } : null,
        daysOrNights: journeyWithDates ? null : journeyDaysCount
    }
}

const setJourneyDuration = (
    journeyWithDates: boolean,
    journeyDates: JourneyDatesProps,
    journeyDaysCount: number
) => {
    const {startDate: journeyStartDate, endDate: journeyEndDate} = journeyDates
    return {
        dates: journeyWithDates ? {
            startDate: journeyStartDate,
            endDate: journeyEndDate
        } : null,
        daysOrNights: journeyWithDates ? null : journeyDaysCount
    }
}


const PersonCountDateForm = ({
                                 defaultValues,
                                 validateForm,
                                 onFormIsValid,
                                 setCalendarOpen,
                                 setSelectedJourneyDuration,
                             }: PersonCountDateFormProps) => {

    const [formData, setFormData] = useState<PersonCountDateFormFormDataProps>({
        journeyDates: {
            startDate: DATE_RANGE_PICKER_START_DATE,
            endDate: DATE_RANGE_PICKER_END_DATE,
        },
        personCount: defaultValues.personCount ?? MIN_NUMBER_PERSON_COUNT_JOURNEY
    })
    const {journeyDates} = formData
    const [journeyDaysCount, setJourneyDaysCount] = useState(DATE_RANGE_PICKER_START_COUNT)
    const [journeyWithDates, setJourneyWithDates] = useState(DATE_RANGE_PICKER_START_USE_DATES)
    const [personCountError, setPersonCountError] = useState(false)
    const [personCount, setPersonCount] = useState(defaultValues.personCount ?? MIN_NUMBER_PERSON_COUNT_JOURNEY)
    const {t} = useTranslation()

    useEffect(() => {
        const {journeyDuration} = defaultValues
        if (journeyDuration !== null) {
            const {dates, daysOrNights} = journeyDuration
            if (dates) {
                const {startDate, endDate} = dates
                setJourneyWithDates(true)
                _updateFormData("journeyDates", {startDate, endDate})
            }
            if (daysOrNights !== null) setJourneyDaysCount(daysOrNights)
        }
    }, [])

    useEffect(() => {
        setSelectedJourneyDuration && setSelectedJourneyDuration(setJourneyDuration(journeyWithDates, journeyDates, journeyDaysCount))
    }, [journeyDates, journeyWithDates, journeyDaysCount]);

    useEffect(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER && _validateForm()) onFormIsValid({
            personCount,
            journeyDuration: getJourneyDuration(journeyWithDates, journeyDates, journeyDaysCount)
        })
    }, [validateForm])

    const _updateFormData = <K extends keyof PersonCountDateFormFormDataProps>(key: K, payload: PersonCountDateFormFormDataProps[K]) => setFormData({
        ...formData,
        [key]: payload
    })

    const _validateForm = () => {
        const validPersonCount = personCount <= maxNumberPeople && personCount >= minNumberPeople
        setPersonCountError(!validPersonCount)
        return [validPersonCount].every(Boolean)
    }

    return <div className={'person-count-date-form-container'}>
        <NumberInput
            min={minNumberPeople}
            max={maxNumberPeople}
            error={personCountError}
            aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                min: minNumberPeople,
                max: maxNumberPeople,
                words: t("ERRORS.COMMON.MIN_MAX_PEOPLE_EXCEEDED")
            })}
            injectMaximum={true}
            injectMinimum={true}
            required={true}
            label={"SCREENS.HOTEL.PEOPLE"}
            onBlur={event => setPersonCount(event.target.valueAsNumber)}
            onChange={event => setPersonCount(event.target.valueAsNumber)}
            containerStyle={{flex: 1}}
            value={personCount}/>
        <DateRangePicker
            showOnlyCalendarIcon={false}
            containerClasses={'save-bundle-create-journey-calendar-calendar'}
            minNumberNightsOrDays={MIN_DAYS}
            maxNumberNightsOrDays={MAX_DAYS}
            numberInputAriaErrorMessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                min: MIN_DAYS,
                max: MAX_DAYS,
                words: t("ERRORS.COMMON.MIN_MAX_DAYS_EXCEEDED")
            })}
            setCalendarOpen={value => setCalendarOpen && setCalendarOpen(value)}
            isNights={false}
            setDateRange={(dateRangeObject, _) => _updateFormData("journeyDates", {
                startDate: dateRangeObject.startDate,
                endDate: dateRangeObject.endDate,
            })}
            setUseDates={setJourneyWithDates}
            setNightsDaysCount={(_, value) => setJourneyDaysCount(value)}/>
    </div>
}

export default PersonCountDateForm